import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  ArrowDropDown,
  Autorenew,
  // Calculate,
  Cancel,
  CheckCircleOutline,
  Description,
  Email,
  EventRepeat,
  FileDownloadOutlined,
  Flag,
  Gavel,
  GridOn,
  Height,
  MoreHoriz,
  PictureAsPdf,
  PriceCheck,
  Print,
  ReportProblemOutlined,
  UnfoldLess,
  UploadFile,
  Visibility,
} from '@mui/icons-material';

const EXPORT_SECTIONS = [
  { label: 'XLS', items: [{ id: 'downloadCases', label: 'Download Cases', icon: <GridOn fontSize="small" /> }] },
  {
    label: 'PDF',
    items: [
      { id: 'downloadWorkups', label: 'Download Workups', icon: <PictureAsPdf fontSize="small" /> },
      { id: 'downloadStips', label: 'Download Stips', icon: <Description fontSize="small" /> },
    ],
  },
];

const ACTION_SECTIONS = [
  {
    label: 'Negotiation',
    items: [
      { id: 'markReviewed', label: 'Mark Reviewed', icon: <CheckCircleOutline fontSize="small" /> },
      { id: 'flagJurisdictional', label: 'Jurisdictional doc request', icon: <ReportProblemOutlined fontSize="small" /> },
      { id: 'flagCase', label: 'Do not file at SCAR', icon: <Flag fontSize="small" /> },
      // { id: 'generateOffers', label: 'Generate Algorithmic Offers', icon: <Calculate fontSize="small" /> },
      { id: 'sendOffer', label: 'Send Offer', icon: <Email fontSize="small" /> },
      { id: 'cancelOffer', label: 'Cancel Offer', icon: <Cancel fontSize="small" /> },
    ],
  },
  {
    label: 'Settlement',
    items: [
      { id: 'settleCase', label: 'Settle Case', icon: <Gavel fontSize="small" /> },
      { id: 'sendSettlement', label: 'Send Settlement', icon: <Email fontSize="small" /> },
    ],
  },
  {
    label: 'Court',
    items: [
      { id: 'uploadEvidence', label: 'Upload Evidence', icon: <UploadFile fontSize="small" /> },
      { id: 'printEvidence', label: 'Print Evidence', icon: <Print fontSize="small" /> },
      { id: 'adjournCases', label: 'Open Adjourn Case', icon: <EventRepeat fontSize="small" /> },
    ],
  },
];

const ADMIN_SECTION = {
  label: 'Admin',
  items: [{
    id: 'training',
    label: 'Update Training Data',
    icon: <Box component="span" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#ff9800', lineHeight: 1 }}>T</Box>,
  }],
};

const MORE_OPTIONS_ITEMS = [
  { id: 'refreshAlgoOffers', label: 'Refresh Algo Offers', icon: <Autorenew fontSize="small" /> },
  { id: 'setOffersToWorkups', label: 'Set Offers to Workups', icon: <PriceCheck fontSize="small" /> },
];

const ADMIN_SETTINGS_ITEMS = [
  { id: 'adminSettings', header: 'Admin settings' },
  { id: 'enterTownView', label: 'Swap to Town View', icon: <Visibility fontSize="small" /> },
];

const SUBHEADER_SX = {
  fontSize: '0.75rem',
  fontWeight: 500,
  letterSpacing: '0.02em',
  lineHeight: 1.5,
  color: 'text.secondary',
  bgcolor: 'transparent',
  px: 2,
  pt: 1,
  pb: 0.25,
  '&:first-of-type': { pt: 0.5 },
};

const TOGGLE_ICON_SX = { mr: 0.5, fontSize: 18 };

const DISABLED_CONTROL_SX = {
  backgroundColor: 'transparent !important',
  color: 'rgba(0, 0, 0, 0.26) !important',
  borderColor: 'rgba(0, 0, 0, 0.16)',
  opacity: 0.72,
  cursor: 'default',
};

const TOOLBAR_BUTTON_SX = {
  height: 32,
  flexShrink: 0,
  py: 0,
  lineHeight: 1,
  alignItems: 'center',
  '&::before': { content: 'none' },
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    my: 0,
    display: 'inline-flex',
    alignItems: 'center',
  },
  '&:disabled': DISABLED_CONTROL_SX,
};

// Span keeps the tooltip available when the toggle is disabled. ToggleButton still
// reads ToggleButtonGroup context, so selected state and grouping classes apply.
function TooltipToggleButton({ title, ...props }) {
  return (
    <Tooltip title={title} arrow>
      <span style={{ display: 'inline-flex' }}>
        <ToggleButton {...props} />
      </span>
    </Tooltip>
  );
}

function SectionedMenu({ anchorEl, onClose, sections, handlers, disabled, tooltips = {}, labels = {} }) {
  const [flyout, setFlyout] = useState({ anchorEl: null, items: [] });
  const closeFlyout = () => setFlyout((prev) => ({ ...prev, anchorEl: null }));
  const close = () => {
    closeFlyout();
    onClose();
  };

  const renderItem = (item) => {
    if (item.header) {
      return [
        <Divider key={`${item.id}-divider`} />,
        <ListSubheader key={item.id} disableSticky sx={SUBHEADER_SX}>{item.header}</ListSubheader>,
      ];
    }
    const tip = tooltips[item.id];
    const itemDisabled = Boolean(disabled[item.id]);
    const menuItem = (
      <MenuItem
        key={item.id}
        dense
        disabled={itemDisabled}
        selected={Boolean(item.items) && Boolean(flyout.anchorEl)}
        sx={tip && itemDisabled ? { '&.Mui-disabled': { pointerEvents: 'auto' } } : undefined}
        onClick={(e) => {
          if (itemDisabled) return;
          if (item.items) return setFlyout({ anchorEl: e.currentTarget, items: item.items });
          close();
          handlers[item.id]?.();
        }}
      >
        <ListItemText>{labels[item.id] || item.label}</ListItemText>
        <ListItemIcon sx={{ minWidth: 0, ml: 3, justifyContent: 'flex-end', '& .MuiSvgIcon-root': { fontSize: 18 } }}>{item.icon}</ListItemIcon>
      </MenuItem>
    );
    if (!tip) return menuItem;
    return (
      <Tooltip key={item.id} title={tip} placement="left">
        {menuItem}
      </Tooltip>
    );
  };

  const children = sections.flatMap((section, i) => [
    i > 0 ? <Divider key={`divider-${i}`} /> : null,
    section.label ? (
      <ListSubheader key={`header-${i}`} disableSticky sx={SUBHEADER_SX}>
        {section.label}
      </ListSubheader>
    ) : null,
    ...section.items.map(renderItem),
  ]).filter(Boolean);

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
      >
        {children}
      </Menu>
      <Menu
        anchorEl={flyout.anchorEl}
        open={Boolean(flyout.anchorEl)}
        onClose={closeFlyout}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        {flyout.items.map(renderItem)}
      </Menu>
    </>
  );
}

export default function CaseGridToolbar({
  filters,
  selectedCount = 0,
  avMode = false,
  onAvModeChange,
  avDisabled = false,
  avTitle = 'Assessed Value Mode',
  showOfferRounds = false,
  onShowOfferRoundsChange,
  showRoundsToggle = true,
  roundsDisabled = false,
  roundsTitle = 'Show Offer Rounds',
  handlers = {},
  disabled = {},
  tooltips = {},
  labels = {},
  showDoNotFileScar = false,
  showTraining = false,
  showAdminSettings = false,
  actionsLabel,
  actionsBusy = false,
  actionsDisabled = false,
  actionsTitle = '',
  exportBusy = false,
  exportDisabled = false,
  exportTitle = '',
}) {
  const [exportAnchor, setExportAnchor] = useState(null);
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const viewValue = [avMode && 'av', showOfferRounds && 'rounds'].filter(Boolean);
  const moreSection = {
    items: [{
      id: 'moreOptions',
      label: 'More options',
      icon: <MoreHoriz fontSize="small" />,
      items: [...MORE_OPTIONS_ITEMS, ...(showAdminSettings ? ADMIN_SETTINGS_ITEMS : [])],
    }],
  };
  const actionSections = [
    ...ACTION_SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter((item) => item.id !== 'flagCase' || showDoNotFileScar),
    })),
    ...(showTraining ? [ADMIN_SECTION] : []),
    moreSection,
  ];
  const resolvedActionsLabel = actionsLabel ?? (selectedCount > 0 ? `Actions (${selectedCount})` : 'Actions');

  const handleViewChange = (_event, next) => {
    const selected = Array.isArray(next) ? next : [];
    const nextAv = selected.includes('av');
    const nextRounds = selected.includes('rounds');
    if (nextAv !== avMode) onAvModeChange?.(nextAv);
    if (nextRounds !== showOfferRounds) onShowOfferRoundsChange?.(nextRounds);
  };

  const exportButton = (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      endIcon={exportBusy ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlined />}
      disabled={exportDisabled}
      onClick={(e) => setExportAnchor(e.currentTarget)}
      sx={TOOLBAR_BUTTON_SX}
    >
      Download
    </Button>
  );

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {filters}
      </Box>

      {filters && <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />}

      <ToggleButtonGroup
        size="small"
        color="primary"
        value={viewValue}
        onChange={handleViewChange}
        aria-label="View options"
        sx={{
          height: 32,
          flexShrink: 0,
          '& .MuiToggleButton-root.Mui-selected': {
            backgroundColor: 'transparent',
            color: 'primary.main',
            borderColor: 'primary.main',
            '&:hover': { backgroundColor: 'transparent' },
          },
          '& .MuiToggleButton-root:disabled': DISABLED_CONTROL_SX,
        }}
      >
        <TooltipToggleButton value="av" title={avTitle} disabled={avDisabled} sx={{ px: 1.5, py: 0, height: 32 }}>
          AV
        </TooltipToggleButton>
        {showRoundsToggle && (
          <TooltipToggleButton value="rounds" title={roundsTitle} disabled={roundsDisabled} sx={{ px: 1.5, py: 0, height: 32 }}>
            {showOfferRounds ? (
              <UnfoldLess sx={{ ...TOGGLE_ICON_SX, transform: 'rotate(90deg)' }} />
            ) : (
              <Height sx={{ ...TOGGLE_ICON_SX, transform: 'rotate(90deg)' }} />
            )}
            Rounds
          </TooltipToggleButton>
        )}
      </ToggleButtonGroup>

      <Box sx={{ flexGrow: 1 }} />

      {exportTitle ? (
        <Tooltip title={exportTitle} arrow>
          <span style={{ display: 'inline-flex' }}>{exportButton}</span>
        </Tooltip>
      ) : exportButton}

      <Tooltip title={actionsTitle} arrow disableHoverListener={!actionsTitle}>
        <span style={{ display: 'inline-flex' }}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            endIcon={actionsBusy ? <CircularProgress size={16} color="inherit" /> : <ArrowDropDown />}
            disabled={actionsDisabled}
            onClick={(e) => setActionsAnchor(e.currentTarget)}
            sx={TOOLBAR_BUTTON_SX}
          >
            {resolvedActionsLabel}
          </Button>
        </span>
      </Tooltip>

      <SectionedMenu
        anchorEl={exportAnchor}
        onClose={() => setExportAnchor(null)}
        sections={EXPORT_SECTIONS}
        handlers={handlers}
        disabled={disabled}
        tooltips={tooltips}
        labels={labels}
      />
      <SectionedMenu
        anchorEl={actionsAnchor}
        onClose={() => setActionsAnchor(null)}
        sections={actionSections}
        handlers={handlers}
        disabled={disabled}
        tooltips={tooltips}
        labels={labels}
      />
    </Stack>
  );
}
